import { useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import type { Ref } from 'react';
import { useSession } from '../session';
import { ImageSizer } from './ImageSizer';
import { cleanPasted, escapeText, textToHtml, tidy } from './richHtml';
import { dataURL, isImage, nameOf, uploadImage } from './upload';
import type { Owner } from './upload';

export type { Owner };

export interface RichTextHandle {
  /** The body to send, or '' when nothing was written. */
  html(): string;
  clear(): void;
}

/** The one place anything is written: a comment, a new task, a task being
 *  reworded.
 *
 *  It edits the HTML in place, the way CKEditor did, so a pasted screenshot
 *  is a picture in the text and not a line of markup standing in for one. It
 *  is a contenteditable with execCommand rather than an editor library on
 *  purpose: a library keeps only the markup its schema knows, and opening a
 *  migrated body with a table or a mention in it to fix a typo would have
 *  thrown those away. execCommand is deprecated and has no replacement;
 *  every browser still runs it, and it keeps the browser's own undo.
 *
 *  Uncontrolled: React rendering the HTML would put the caret back at the
 *  start on every keystroke. The form asks for the result when it submits. */
export function RichText({
  ref, labelledBy, initialHtml, owner, compact, onPendingChange, onEmptyChange,
}: {
  ref?: Ref<RichTextHandle>;
  labelledBy: string;
  initialHtml?: string | null;
  owner: Owner;
  /** A comment box starts small; a description has room. */
  compact?: boolean;
  /** True while an image is still on its way, so the form can hold its
   *  submit button rather than post without it. */
  onPendingChange?: (pending: boolean) => void;
  onEmptyChange?: (empty: boolean) => void;
}) {
  const { t } = useSession();
  const box = useRef<HTMLDivElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  // The image clicked on, which gets a frame to resize it by.
  const [selected, setSelected] = useState<HTMLImageElement | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  // The images still on their way, by number. They are dimmed by a rule
  // written outside the box rather than by marking them inside it: Chrome's
  // undo history cannot follow a change it did not make to a node it
  // inserted, and setting an attribute on the image once it had uploaded made
  // ⌘Z undo the typing before it instead of the image.
  const [uploading, setUploading] = useState<string[]>([]);
  const pending = uploading.length;
  const boxId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const [failed, setFailed] = useState(false);
  const [dropping, setDropping] = useState(false);
  const [empty, setEmpty] = useState(!initialHtml);
  const [active, setActive] = useState<Record<string, boolean>>({});
  // Each image put in, by the number it carries, and where it ended up. The
  // picture on screen stays the local copy until the body is saved: the
  // server answers 404 for an upload no saved body embeds yet, which is right,
  // and swapping the address in early broke every image the moment it landed.
  // tidy() puts the real address in on the way out.
  const uploaded = useRef(new Map<string, string>());
  // The numbers this box handed out and has not given up on. A copy of one of
  // its images pasted back in keeps its number only if it is here; the box's
  // id goes in front so another editor on the page cannot claim it.
  const issued = useRef(new Set<string>());
  const counter = useRef(0);
  // A drag that started in the box is a move, which the browser does itself.
  const draggingInside = useRef(false);

  useEffect(() => {
    if (box.current) box.current.innerHTML = initialHtml ?? '';
    refresh();
  }, [initialHtml]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { onPendingChange?.(pending > 0); }, [pending, onPendingChange]);
  useEffect(() => { onEmptyChange?.(empty); }, [empty, onEmptyChange]);

  useImperativeHandle(ref, () => ({
    html: () => (box.current ? tidy(box.current, uploaded.current) : ''),
    clear: () => {
      if (box.current) box.current.innerHTML = '';
      uploaded.current.clear();
      issued.current.clear();
      setFailed(false);
      refresh();
    },
  }));

  /** Whether there is anything in the box, and which buttons are on. */
  function refresh() {
    const el = box.current;
    if (!el) return;
    setEmpty(!el.textContent?.trim() && !el.querySelector('img'));
    if (!el.contains(document.getSelection()?.anchorNode ?? null)) return;
    setActive({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList: document.queryCommandState('insertOrderedList'),
    });
  }

  useEffect(() => {
    document.addEventListener('selectionchange', refresh);
    return () => document.removeEventListener('selectionchange', refresh);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  /** Puts the caret in the box if it is somewhere else, at the end. */
  function caretInside(): void {
    const el = box.current!;
    const selection = document.getSelection();
    if (selection && el.contains(selection.anchorNode)) return;
    el.focus();
    startParagraph();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  /** An empty box gets a paragraph to type into. Without one the first line
   *  sits loose in the box, and Chrome then builds the next block around it:
   *  a list typed after it came out nested inside a <p>. */
  function startParagraph() {
    document.execCommand('defaultParagraphSeparator', false, 'p');
    const el = box.current;
    if (!el || el.firstElementChild || el.textContent) return;
    el.innerHTML = '<p><br></p>';
    const range = document.createRange();
    range.setStart(el.firstChild!, 0);
    range.collapse(true);
    const selection = document.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  function exec(command: string, value?: string) {
    caretInside();
    // Enter makes paragraphs, not the <div>s Chrome makes by default.
    document.execCommand('defaultParagraphSeparator', false, 'p');
    document.execCommand(command, false, value);
    refresh();
  }

  /** Gives an image a new width, as one step undo can take back.
   *
   *  Setting the attribute on the image in place looked simpler and was not:
   *  Chrome's undo cannot follow a change it did not make, and the next ⌘Z
   *  undid the wrong thing. So the image is selected and replaced by a copy
   *  of itself with the new width, through the same insertHTML that put it
   *  there. */
  function resize(img: HTMLImageElement, percent: number) {
    const el = box.current;
    if (!el || !el.contains(img)) return;
    const copy = img.cloneNode() as HTMLImageElement;
    copy.setAttribute('width', `${percent}%`);
    // A migrated image carries its old pixel height, which would stretch it.
    copy.removeAttribute('height');
    copy.removeAttribute('style');

    el.focus();
    const selection = document.getSelection();
    if (!selection) return;
    const range = document.createRange();
    range.selectNode(img);
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand('insertHTML', false, copy.outerHTML);

    // The image in the box is a new node now, just before the caret.
    const at = selection.rangeCount ? selection.getRangeAt(0) : null;
    const before = at?.startContainer.childNodes[at.startOffset - 1];
    const next = before instanceof HTMLImageElement
      ? before
      : [...el.querySelectorAll('img')].find((i) =>
        i.getAttribute('src') === copy.getAttribute('src') && i.getAttribute('width') === copy.getAttribute('width'));
    choose(next ?? null);
    refresh();
  }

  /** Selects an image as a whole, so Delete takes it away and the frame
   *  shows. */
  function choose(img: HTMLImageElement | null) {
    setSelected(img);
    if (!img) return;
    const selection = document.getSelection();
    const range = document.createRange();
    range.selectNode(img);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  function link() {
    caretInside();
    const selection = document.getSelection();
    const saved = selection?.rangeCount ? selection.getRangeAt(0).cloneRange() : null;
    const at = selection?.anchorNode;
    const anchor = (at instanceof Element ? at : at?.parentElement)?.closest('a');
    const answer = window.prompt(t.linkAddress, anchor?.getAttribute('href') ?? 'https://');
    // The prompt can take the selection with it.
    if (saved && selection) { selection.removeAllRanges(); selection.addRange(saved); }
    if (answer === null) return;
    // With the caret in a link, the address being changed is that link's:
    // select the whole of it, or a new link would go in inside the old one.
    if (anchor && box.current?.contains(anchor) && selection?.isCollapsed) {
      const whole = document.createRange();
      whole.selectNodeContents(anchor);
      selection.removeAllRanges();
      selection.addRange(whole);
    }
    const href = answer.trim();
    if (!href || href === 'https://') { exec('unlink'); return; }
    const url = /^(https?:|mailto:|\/)/i.test(href) ? href : href.includes('@') ? `mailto:${href}` : `https://${href}`;
    if (selection && !selection.isCollapsed) exec('createLink', url);
    else exec('insertHTML', `<a href="${escapeText(url)}">${escapeText(href)}</a>`);
  }

  /** Images go in at once, shown from the file, and upload behind it. */
  async function addImages(files: File[]) {
    const images = files.filter(isImage);
    if (images.length === 0) return;
    setFailed(false);
    caretInside();
    // Reading the files is quick but not instant, and the caret has to be
    // where it was when they were dropped.
    const range = document.getSelection()?.getRangeAt(0).cloneRange();

    const items = await Promise.all(images.map(async (file) => ({
      file, id: `${boxId}-${++counter.current}`, preview: await dataURL(file),
    })));

    const selection = document.getSelection();
    if (range && selection) { selection.removeAllRanges(); selection.addRange(range); }
    exec('insertHTML', items.map((it) =>
      `<img src="${it.preview}" alt="${escapeText(nameOf(it.file))}" data-upload="${it.id}">`).join(''));

    for (const it of items) issued.current.add(it.id);
    setUploading((ids) => [...ids, ...items.map((it) => it.id)]);
    for (const it of items) {
      uploadImage(it.file, owner)
        .then((src) => { uploaded.current.set(it.id, src); })
        .catch((err: unknown) => {
          // The message on screen is the same whatever went wrong; the
          // console says which.
          console.error(`image upload failed for ${nameOf(it.file)}:`, err);
          setFailed(true);
          issued.current.delete(it.id);
          // Every copy of it, if it was pasted again while it uploaded.
          for (const img of box.current?.querySelectorAll(`img[data-upload="${it.id}"]`) ?? []) img.remove();
          refresh();
        })
        .finally(() => setUploading((ids) => ids.filter((id) => id !== it.id)));
    }
  }

  /** A screenshot is only a file; Word puts a picture of the text on the
   *  clipboard next to the text itself, and the text is what was meant. */
  function insertTransfer(data: DataTransfer): boolean {
    const html = data.getData('text/html');
    const text = data.getData('text/plain');
    const files = [...data.files].filter(isImage);
    const hasWords = !!text.trim() || !!html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

    if (files.length > 0 && !hasWords) {
      void addImages(files);
      return true;
    }
    if (html) {
      const cleaned = cleanPasted(html, (id) => issued.current.has(id));
      if (cleaned) exec('insertHTML', cleaned);
      return true;
    }
    if (text) {
      if (text.includes('\n')) exec('insertHTML', textToHtml(text));
      else exec('insertText', text);
      return true;
    }
    return false;
  }

  const tool = (command: string, label: string, glyph: React.ReactNode) => (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={!!active[command]}
      // Keeps the selection in the box; a click would move focus first.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => exec(command)}
    >
      {glyph}
    </button>
  );

  return (
    <div
      ref={wrap}
      className={dropping ? 'rich dropping' : 'rich'}
      onBlur={(e) => {
        // Leaving the editor altogether; a click on the frame's buttons keeps
        // focus in the box, so does not count.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setSelected(null);
      }}
    >
      <div className="rich-tools" role="toolbar" aria-label={t.formatting}>
        {tool('bold', t.bold, <strong className="glyph">B</strong>)}
        {tool('italic', t.italic, <em className="glyph">I</em>)}
        {tool('insertUnorderedList', t.bulletList, <ListIcon numbered={false} />)}
        {tool('insertOrderedList', t.numberedList, <ListIcon numbered />)}
        <button type="button" aria-label={t.link} title={`${t.link} (⌘K)`}
          onMouseDown={(e) => e.preventDefault()} onClick={link}>
          {t.link}
        </button>
        <button type="button" aria-label={t.image} title={t.image}
          onMouseDown={(e) => e.preventDefault()} onClick={() => picker.current?.click()}>
          {t.image}
        </button>
        <input
          ref={picker}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            void addImages([...(e.target.files ?? [])]);
            e.target.value = '';
          }}
        />
      </div>
      {uploading.length > 0 && (
        <style>{uploading.map((id) => `#${boxId} img[data-upload="${id}"]`).join(', ')} {'{ opacity: 0.5; }'}</style>
      )}
      <div
        ref={box}
        id={boxId}
        className={compact ? 'body editor compact' : 'body editor'}
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-labelledby={labelledBy}
        data-empty={empty ? '' : undefined}
        data-placeholder={t.imageHint}
        suppressContentEditableWarning
        onFocus={() => {
          startParagraph();
          // Firefox draws its own resize handles on images in an editable box,
          // which write pixel sizes into style attributes that are thrown away.
          try { document.execCommand('enableObjectResizing', false, 'false'); } catch { /* not Firefox */ }
        }}
        onInput={() => {
          if (selected && !box.current?.contains(selected)) setSelected(null);
          refresh();
        }}
        onClick={(e) => {
          const target = e.target as HTMLElement;
          choose(target instanceof HTMLImageElement && box.current?.contains(target) ? target : null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && selected) {
            e.preventDefault();
            setSelected(null);
            return;
          }
          if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
            e.preventDefault();
            link();
          }
        }}
        onPaste={(e) => {
          if (insertTransfer(e.clipboardData)) e.preventDefault();
        }}
        onDragStart={() => { draggingInside.current = true; }}
        onDragEnd={() => { draggingInside.current = false; }}
        onDragOver={(e) => {
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setDropping(true);
        }}
        onDragLeave={() => setDropping(false)}
        onDrop={(e) => {
          setDropping(false);
          // Moving something within the box: left to the browser, which takes
          // it away from where it was. Putting it in here would copy it.
          if (draggingInside.current) return;
          e.preventDefault();
          // The caret goes where the thing was dropped, not where it was.
          const at = caretAt(e.clientX, e.clientY);
          const selection = document.getSelection();
          if (at && selection) { selection.removeAllRanges(); selection.addRange(at); }
          insertTransfer(e.dataTransfer);
        }}
      />
      {selected && wrap.current && box.current?.contains(selected) && (
        <ImageSizer img={selected} wrap={wrap.current} box={box.current} onResize={resize} />
      )}
      {failed && <p className="rich-error" role="alert">{t.uploadFailed}</p>}
    </div>
  );
}

/** Three lines with a dot, or a number, in front of each. */
function ListIcon({ numbered }: { numbered: boolean }) {
  return (
    <svg width="18" height="14" viewBox="0 0 18 14" aria-hidden="true" fill="currentColor">
      {[1.5, 7, 12.5].map((y, i) => (
        <g key={y}>
          {numbered
            ? <text x="0" y={y + 2} fontSize="5" fontFamily="var(--font-display)">{i + 1}</text>
            : <circle cx="1.75" cy={y} r="1.25" />}
          <rect x="6" y={y - 0.5} width="12" height="1" />
        </g>
      ))}
    </svg>
  );
}

function caretAt(x: number, y: number): Range | null {
  if (document.caretRangeFromPoint) return document.caretRangeFromPoint(x, y);
  const position = (document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
  }).caretPositionFromPoint?.(x, y);
  if (!position) return null;
  const range = document.createRange();
  range.setStart(position.offsetNode, position.offset);
  range.collapse(true);
  return range;
}
